/**
 * RomanNumeralAnalysis — display chord symbols WITH their Roman numeral
 * function above each one. Click any chord to hear it on the piano.
 *
 * Used to teach why a progression works, not just what notes it contains.
 * Built from Jason's handwritten chord charts + transcripts.
 */

import { playNow, kickAudio } from "@/audio/piano";
import { chordSymbolToPitches } from "@/theory/chordSymbol";

export interface RomanChord {
  /** Chord symbol as written ("Dbmaj7", "F#m", "G/B", "Cm7♭5") */
  chord: string;
  /** Roman numeral with quality / inversion ("I", "vi", "V/V", "♭VII", "I/3") */
  rn: string;
  /** Optional secondary label (e.g. "Tonic", "Pre-dominant"). */
  function?: "Tonic" | "Pre-dominant" | "Dominant" | "Mediant" | "Submediant" | "Borrowed" | "Secondary V" | "Tension";
  /** Notes to play when clicked. Pass MIDI pitch names; when omitted, the chord
   *  symbol itself is read and voiced (see theory/chordSymbol). */
  pitches?: string[];
}

const FN_COLOR: Record<NonNullable<RomanChord["function"]>, string> = {
  Tonic:           "text-emerald-300",
  "Pre-dominant":  "text-sky-300",
  Dominant:        "text-rose-300",
  Mediant:         "text-violet-300",
  Submediant:      "text-violet-300",
  Borrowed:        "text-amber-300",
  "Secondary V":   "text-fuchsia-300",
  Tension:         "text-rose-400",
};

interface RomanNumeralAnalysisProps {
  /** Section label — "Verse", "Chorus", "Bridge", "Pre-chorus". */
  label: string;
  /** Optional explanation paragraph below the chord row. */
  caption?: string;
  /** The progression — one entry per chord beat. Use `bar: true` to insert a barline. */
  chords: (RomanChord | { bar: true })[];
  /** If chords have only roman numerals (no chord symbol), set to false to skip the chord-name row. */
  showChordSymbols?: boolean;
}

export function RomanNumeralAnalysis({
  label,
  caption,
  chords,
  showChordSymbols = true,
}: RomanNumeralAnalysisProps) {
  return (
    <div className="my-4 rounded-xl border border-amber-500/30 bg-gradient-to-br from-amber-500/[0.04] to-zinc-900/[0.04] p-4">
      <div className="mb-2 flex items-baseline justify-between">
        <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-amber-300">{label}</div>
        <div className="text-[10px] uppercase tracking-wider text-zinc-500">click a chord to hear it</div>
      </div>
      <div className="overflow-x-auto">
        <div className="flex min-w-fit items-stretch gap-1.5 py-2">
          {chords.map((c, i) => {
            if ("bar" in c) {
              return <div key={i} className="w-px self-stretch bg-amber-500/40" aria-hidden />;
            }
            // Hand-voiced notes win; otherwise sound exactly what the symbol names.
            const notes = c.pitches?.length ? c.pitches : chordSymbolToPitches(c.chord);
            return (
              <button
                key={i}
                data-notes={notes?.length || undefined}
                onClick={async () => {
                  if (!notes?.length) return;
                  kickAudio();
                  await playNow(notes, "1n", 0.7);
                }}
                disabled={!notes?.length}
                className="group flex flex-col items-center justify-between rounded-lg border border-zinc-700/70 bg-zinc-900/40 px-3 py-2 text-center transition hover:border-amber-400/60 hover:bg-amber-500/5 disabled:cursor-default disabled:opacity-90"
              >
                <div className={`text-[10px] font-semibold uppercase tracking-wider ${c.function ? FN_COLOR[c.function] : "text-zinc-500"}`}>
                  {c.rn}
                </div>
                {showChordSymbols && (
                  <div
                    className="mt-1 font-mt text-lg font-semibold text-amber-200"
                    style={{ fontFamily: '"Cormorant Garamond", "Spectral", Georgia, serif' }}
                  >
                    {c.chord}
                  </div>
                )}
                {c.function && (
                  <div className="mt-0.5 text-[9px] uppercase tracking-wider text-zinc-500 opacity-0 transition group-hover:opacity-100">
                    {c.function}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>
      {caption && <div className="mt-2 text-[13px] leading-relaxed text-nsm-dim">{caption}</div>}
    </div>
  );
}
